/**
 * TimeSlotPicker Component
 * V226: 통합 시간 슬롯 선택 컴포넌트
 *
 * Features:
 * - 시간 슬롯 그리드 표시
 * - 마감/예약 상태 표시
 * - 오전/오후 구분
 * - 커스텀 시간 범위 지원
 */

import { useMemo } from 'react';
import { Clock, Lock, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { TimeSlotPickerProps, TimeSlot } from './types';
import { generateTimeSlots, DEFAULT_TIME_CONFIG } from './constants';

export function TimeSlotPicker({
    selected,
    onSelect,
    slots,
    closedSlots = [],
    reservedSlots = [],
    startTime = DEFAULT_TIME_CONFIG.startTime,
    endTime = DEFAULT_TIME_CONFIG.endTime,
    interval = DEFAULT_TIME_CONFIG.interval,
    columns = 4,
    disabled = false,
    className,
}: TimeSlotPickerProps) {
    // 시간 슬롯 생성
    const timeSlots = useMemo(() => {
        if (slots) return slots;
        return generateTimeSlots(startTime, endTime, interval, closedSlots, reservedSlots);
    }, [slots, startTime, endTime, interval, closedSlots, reservedSlots]);

    // 오전/오후 그룹화
    const groupedSlots = useMemo(() => {
        const am: TimeSlot[] = [];
        const pm: TimeSlot[] = [];

        timeSlots.forEach((slot) => {
            const hour = parseInt(slot.value.split(':')[0], 10);
            if (hour < 12) {
                am.push(slot);
            } else {
                pm.push(slot);
            }
        });

        return { am, pm };
    }, [timeSlots]);

    const handleSelect = (slot: TimeSlot) => {
        if (disabled || slot.disabled || slot.closed) return;
        onSelect(slot.value);
    };

    const renderSlot = (slot: TimeSlot) => {
        const isSelected = selected === slot.value;
        const isClosed = slot.closed;
        const isReserved = slot.reserved;
        const isDisabled = disabled || slot.disabled || isClosed;

        return (
            <Button
                key={slot.value}
                variant={isSelected ? 'default' : 'outline'}
                size="sm"
                disabled={isDisabled}
                onClick={() => handleSelect(slot)}
                className={cn(
                    'h-10 relative transition-all',
                    isSelected && 'bg-primary text-primary-foreground shadow-md',
                    isClosed && 'bg-muted text-muted-foreground border-border cursor-not-allowed',
                    isReserved && !isSelected && 'bg-warning/10 border-warning/30 text-warning',
                    !isDisabled && !isSelected && 'hover:bg-accent hover:border-primary/50'
                )}
            >
                <span className="text-sm font-medium">{slot.value}</span>
                {isClosed && (
                    <Lock className="absolute top-0.5 right-0.5 h-3 w-3 text-muted-foreground" />
                )}
                {isReserved && !isClosed && (
                    <User className="absolute top-0.5 right-0.5 h-3 w-3 text-warning" />
                )}
            </Button>
        );
    };

    return (
        <div className={cn('space-y-4', className)}>
            {/* 오전 슬롯 */}
            {groupedSlots.am.length > 0 && (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs bg-info/10 text-info border-info/30">
                            오전
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                            {groupedSlots.am.length}개 슬롯
                        </span>
                    </div>
                    <div
                        className={cn('grid gap-2')}
                        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
                    >
                        {groupedSlots.am.map(renderSlot)}
                    </div>
                </div>
            )}

            {/* 오후 슬롯 */}
            {groupedSlots.pm.length > 0 && (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/30">
                            오후
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                            {groupedSlots.pm.length}개 슬롯
                        </span>
                    </div>
                    <div
                        className={cn('grid gap-2')}
                        style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}
                    >
                        {groupedSlots.pm.map(renderSlot)}
                    </div>
                </div>
            )}

            {/* 범례 */}
            <div className="flex items-center justify-center gap-4 pt-2 border-t text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-primary" />
                    <span>선택됨</span>
                </div>
                {closedSlots.length > 0 && (
                    <div className="flex items-center gap-1">
                        <Lock className="h-3 w-3 text-muted-foreground" />
                        <span>마감</span>
                    </div>
                )}
                {reservedSlots.length > 0 && (
                    <div className="flex items-center gap-1">
                        <User className="h-3 w-3 text-warning" />
                        <span>예약 있음</span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TimeSlotPicker;
